import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, money } from '../../utils/format';
import { ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { EnrolDrawer } from './EnrolDrawer';
import { CollectDrawer } from './CollectDrawer';
import { SettleDrawer } from './SettleDrawer';

export interface ChitAccountRow {
  id: string;
  chit_no: string;
  status: 'ACTIVE' | 'MATURED' | 'REDEEMED' | 'BROKEN';
  join_date: string;
  maturity_date: string;
  instalment_amount: string;
  paid_amount: string;
  paid_weight: string;
  paid_count: number;
  scheme_name: string;
  instalments: number;
  scheme_type: 'AMOUNT' | 'WEIGHT';
  customer_name: string;
  customer_phone: string | null;
  overdue: number;
}

export interface Instalment {
  instalment_no: number;
  due_date: string;
  due_amount: string;
  status: 'DUE' | 'PAID';
  paid_amount: string | null;
  paid_on: string | null;
  pay_mode: string | null;
  reference_no: string | null;
  rate_per_gram: string | null;
  weight_gms: string | null;
  receipt_no: string | null;
  remarks: string | null;
  received_by: string | null;
  overdue: boolean;
}

export interface Settlement {
  paid: string;
  paidWeight: string;
  bonus: string;
  rateBenefit: string;
  currentRate: string | null;
  redeemable: string;
  breakRefund: string;
  breakPenalty: string;
  canRedeem: boolean;
}

export interface ChitDetail extends ChitAccountRow {
  customer_code: string;
  customer_address: Record<string, string> | null;
  branch_name: string;
  period: string;
  flexible: boolean;
  min_amount: string;
  max_amount: string | null;
  bonus_mode: string;
  bonus_value: string;
  wastage_discount_pct: string;
  making_discount_pct: string;
  break_penalty_pct: string;
  purity_code: string | null;
  metal_code: string | null;
  introducer_name: string | null;
  salesperson_name: string | null;
  nominee_name: string | null;
  nominee_relation: string | null;
  remarks: string | null;
  bonus_amount: string;
  rate_benefit: string;
  redeemed_value: string;
  refund_amount: string;
  penalty_amount: string;
  closing_reason: string | null;
  closed_at: string | null;
  instalments_list?: never;
  instalments: number;
  settlement: Settlement;
}

interface Overview {
  customers: number;
  accounts: number;
  active: number;
  matured: number;
  redeemed: number;
  broken: number;
  total_collected: string;
  total_redeemed: string;
  saved_gold_wt: string;
  saved_silver_wt: string;
  deposits_held: string;
  collected_this_month: string;
  overdue_count: number;
  overdue_amount: string;
  due_this_week: number;
}

interface DueRow {
  account_id: string;
  chit_no: string;
  customer_name: string;
  customer_phone: string | null;
  scheme_name: string;
  instalment_no: number;
  due_date: string;
  due_amount: string;
  days_late: number;
  overdue: boolean;
}

type StatusFilter = '' | 'ACTIVE' | 'MATURED' | 'REDEEMED' | 'BROKEN';

export const STATUS_STYLE: Record<ChitAccountRow['status'], { label: string; cls: string }> = {
  ACTIVE: { label: 'Active', cls: 'bg-success/10 text-success' },
  MATURED: { label: 'Matured', cls: 'bg-gold-100 text-gold-700' },
  REDEEMED: { label: 'Redeemed', cls: 'bg-silver-100 text-ink-soft' },
  BROKEN: { label: 'Closed early', cls: 'bg-danger/10 text-danger' },
};

/**
 * Gold savings — the screen a shop runs its schemes from.
 *
 * The cards across the top are the questions an owner asks: how many savers,
 * how much gold is being saved, and — the one that matters most — how much the
 * shop is holding that belongs to its customers. That figure comes straight
 * from the ledger, so it is the same number an auditor would find.
 *
 * Below, the member list on the left and the selected passbook on the right,
 * with every action a counter needs: take an instalment, print the receipt,
 * undo a mistake, redeem at maturity, close early.
 */
export function ChitsPage() {
  const { can } = usePermission();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('');
  const [schemeId, setSchemeId] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'members' | 'due'>('members');
  const [enrolling, setEnrolling] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [settling, setSettling] = useState<'redeem' | 'break' | 'undo' | null>(null);

  const overview = useQuery({ queryKey: ['chit-overview'], queryFn: () => api.get<Overview>('/chit/overview') });
  const schemes = useQuery({
    queryKey: ['chit-schemes'],
    queryFn: () => api.get<{ data: { id: string; name: string; status: string }[] }>('/chit/schemes'),
  });
  const accounts = useQuery({
    queryKey: ['chit-accounts', status, schemeId, search],
    queryFn: () => {
      const q = new URLSearchParams();
      if (status) q.set('status', status);
      if (schemeId) q.set('schemeId', schemeId);
      if (search) q.set('q', search);
      return api.get<{ data: ChitAccountRow[] }>(`/chit/accounts?${q}`);
    },
  });
  const due = useQuery({
    queryKey: ['chit-due'],
    queryFn: () => api.get<{ data: DueRow[] }>('/chit/due?days=7'),
    enabled: tab === 'due',
  });
  const detail = useQuery({
    queryKey: ['chit-account', selected],
    queryFn: () => api.get<ChitDetail & { instalments: unknown }>(`/chit/accounts/${selected}`),
    enabled: !!selected,
  });

  const o = overview.data;
  const rows = accounts.data?.data ?? [];

  const cards = o
    ? [
        { label: 'Savers', value: String(o.customers), accent: 'bg-silver-600' },
        { label: 'Accounts', value: String(o.accounts), accent: 'bg-velvet' },
        { label: 'Active · Matured · Redeemed · Closed', value: `${o.active} · ${o.matured} · ${o.redeemed} · ${o.broken}`, accent: 'bg-success' },
        { label: 'Gold being saved', value: fine(o.saved_gold_wt), accent: 'bg-gold-500' },
        { label: 'Held for customers', value: money(o.deposits_held), accent: 'bg-gold-700', hint: 'owed to savers — from the ledger' },
        { label: 'Collected this month', value: money(o.collected_this_month), accent: 'bg-success' },
        {
          label: 'Overdue',
          value: `${o.overdue_count}`,
          accent: o.overdue_count > 0 ? 'bg-danger' : 'bg-silver-400',
          hint: o.overdue_count > 0 ? money(o.overdue_amount) : `${o.due_this_week} due this week`,
          onClick: () => setTab('due'),
        },
      ]
    : [];

  return (
    <>
      <PageHeader
        title="Gold savings"
        subtitle="Monthly savings schemes: enrol, collect, and redeem against jewellery."
        actions={
          <>
            {can('chit.scheme') ? (
              <button className="btn-ghost" onClick={() => navigate('/chit-schemes')}>
                Schemes
              </button>
            ) : null}
            {can('chit.enrol') ? (
              <button className="btn-primary" onClick={() => setEnrolling(true)}>
                Enrol a customer
              </button>
            ) : null}
          </>
        }
      />
      <ErrorNotice error={overview.error ?? accounts.error} />

      {/* The overview. */}
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={c.onClick}
            className={`relative overflow-hidden rounded-xl border border-silver-100 bg-surface px-3 pb-3 pt-2.5 text-left shadow-card ${
              c.onClick ? 'hover:shadow-pop' : 'cursor-default'
            }`}
          >
            <p className="text-2xs leading-tight text-ink-soft">{c.label}</p>
            <p className="num mt-1 text-lg font-bold leading-tight">{c.value}</p>
            {c.hint ? <p className="mt-0.5 text-2xs text-ink-faint">{c.hint}</p> : null}
            <span className={`absolute inset-x-0 bottom-0 h-1 ${c.accent}`} />
          </button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
        {/* Left: the members, or who is due. */}
        <aside className="overflow-hidden rounded-xl border border-silver-100 bg-surface shadow-card">
          <div className="flex border-b border-silver-100">
            {(['members', 'due'] as const).map((t) => (
              <button
                key={t}
                className={`flex-1 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-gold-600 text-gold-700' : 'text-ink-soft'}`}
                onClick={() => setTab(t)}
              >
                {t === 'members' ? 'Members' : `Due & overdue${o && o.overdue_count > 0 ? ` (${o.overdue_count})` : ''}`}
              </button>
            ))}
          </div>

          {tab === 'members' ? (
            <>
              <div className="space-y-2 border-b border-silver-100 bg-gold-50/40 p-3">
                <div className="flex flex-wrap gap-1">
                  {(['', 'ACTIVE', 'MATURED', 'REDEEMED', 'BROKEN'] as StatusFilter[]).map((s) => (
                    <button
                      key={s || 'all'}
                      onClick={() => setStatus(s)}
                      className={`rounded-full px-2.5 py-0.5 text-2xs font-medium ${
                        status === s ? 'bg-velvet text-white' : 'bg-surface text-ink-soft hover:bg-gold-100'
                      }`}
                    >
                      {s ? STATUS_STYLE[s].label : 'All'}
                    </button>
                  ))}
                </div>
                <select className="field" value={schemeId} onChange={(e) => setSchemeId(e.target.value)}>
                  <option value="">All schemes</option>
                  {(schemes.data?.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <input
                  className="field"
                  placeholder="Chit no., name or mobile"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {accounts.isLoading ? <Loading rows={5} /> : null}
              <ul className="max-h-[60vh] overflow-y-auto">
                {rows.length === 0 && !accounts.isLoading ? (
                  <li className="px-4 py-8 text-center text-sm text-ink-soft">No savings accounts match.</li>
                ) : null}
                {rows.map((r) => {
                  const pct = Math.round((r.paid_count / Math.max(1, r.instalments)) * 100);
                  return (
                    <li key={r.id}>
                      <button
                        onClick={() => setSelected(r.id)}
                        className={`w-full border-b border-silver-100/70 px-3 py-2.5 text-left transition-colors ${
                          selected === r.id ? 'bg-gold-50' : 'hover:bg-silver-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{r.customer_name}</span>
                          <span className={`rounded px-1.5 py-0.5 text-2xs font-medium ${STATUS_STYLE[r.status].cls}`}>
                            {STATUS_STYLE[r.status].label}
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-center justify-between text-2xs text-ink-soft">
                          <span className="num">
                            {r.chit_no} · {r.scheme_name}
                          </span>
                          {r.overdue > 0 && r.status === 'ACTIVE' ? (
                            <span className="font-medium text-danger">{r.overdue} overdue</span>
                          ) : null}
                        </div>
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-silver-100">
                            <div className="h-full rounded-full bg-gold-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="num text-2xs text-ink-faint">
                            {r.paid_count}/{r.instalments}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <DueList rows={due.data?.data ?? []} loading={due.isLoading} onPick={(id) => { setSelected(id); setTab('members'); }} />
          )}
        </aside>

        {/* Right: the passbook. */}
        <section>
          {!selected ? (
            <div className="grid h-full min-h-[300px] place-items-center rounded-xl border border-dashed border-silver-400 bg-surface p-8 text-center text-sm text-ink-soft">
              Choose a member to see their passbook — or enrol a new customer.
            </div>
          ) : detail.isLoading ? (
            <Loading rows={8} />
          ) : detail.data ? (
            <Passbook
              d={detail.data as unknown as ChitDetail & { instalments: Instalment[] }}
              onCollect={() => setCollecting(true)}
              onSettle={setSettling}
              onReceipt={(n) => navigate(`/chits/${selected}/receipt/${n}`)}
            />
          ) : null}
        </section>
      </div>

      {enrolling ? <EnrolDrawer onClose={() => setEnrolling(false)} onEnrolled={(id) => setSelected(id)} /> : null}
      {collecting && detail.data ? (
        <CollectDrawer
          account={detail.data as unknown as ChitDetail & { instalments: Instalment[] }}
          onClose={() => setCollecting(false)}
          onReceipt={(n) => navigate(`/chits/${selected}/receipt/${n}`)}
        />
      ) : null}
      {settling && detail.data ? (
        <SettleDrawer
          mode={settling}
          account={detail.data as unknown as ChitDetail & { instalments: Instalment[] }}
          onClose={() => setSettling(null)}
        />
      ) : null}
    </>
  );
}

function DueList({ rows, loading, onPick }: { rows: DueRow[]; loading: boolean; onPick: (id: string) => void }) {
  if (loading) return <Loading rows={5} />;
  if (rows.length === 0) {
    return <p className="px-4 py-8 text-center text-sm text-ink-soft">Nobody is due in the next week. Everyone is up to date.</p>;
  }
  return (
    <ul className="max-h-[70vh] overflow-y-auto">
      {rows.map((r) => (
        <li key={r.account_id}>
          <button onClick={() => onPick(r.account_id)} className="w-full border-b border-silver-100/70 px-3 py-2.5 text-left hover:bg-silver-50">
            <div className="flex items-center justify-between">
              <span className="font-medium">{r.customer_name}</span>
              <span className={`num text-sm font-semibold ${r.overdue ? 'text-danger' : 'text-warn'}`}>{money(r.due_amount)}</span>
            </div>
            <div className="mt-0.5 flex items-center justify-between text-2xs text-ink-soft">
              <span className="num">
                #{r.instalment_no} due {date(r.due_date)}
              </span>
              <span className={r.overdue ? 'font-medium text-danger' : ''}>
                {r.days_late > 0 ? `${r.days_late} days late` : r.days_late === 0 ? 'due today' : `in ${-r.days_late} days`}
              </span>
            </div>
            {r.customer_phone ? (
              <a
                href={`tel:${r.customer_phone}`}
                onClick={(e) => e.stopPropagation()}
                className="num mt-1 inline-block text-2xs text-gold-700 underline"
              >
                Call +91 {r.customer_phone}
              </a>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Passbook({
  d,
  onCollect,
  onSettle,
  onReceipt,
}: {
  d: ChitDetail & { instalments: Instalment[] };
  onCollect: () => void;
  onSettle: (m: 'redeem' | 'break' | 'undo') => void;
  onReceipt: (n: number) => void;
}) {
  const { can } = usePermission();
  const schedule = d.instalments;
  const total = schedule.reduce((a, i) => a + Number(i.due_amount), 0);
  const remaining = schedule.filter((i) => i.status === 'DUE').reduce((a, i) => a + Number(i.due_amount), 0);
  const address = d.customer_address ?? {};
  const place = [address['city'], address['state']].filter(Boolean).join(', ');
  const isWeight = d.scheme_type === 'WEIGHT';
  const anyPaid = schedule.some((i) => i.status === 'PAID');
  const bonusText =
    d.bonus_mode === 'PERCENT'
      ? `${d.bonus_value}% of savings`
      : d.bonus_mode === 'ONE_INSTALMENT'
        ? 'one instalment free'
        : d.bonus_mode === 'FIXED'
          ? money(d.bonus_value)
          : 'none';

  return (
    <div className="space-y-4">
      {/* Who and which scheme. */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-silver-100 bg-surface p-4 shadow-card">
          <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-soft">Customer</p>
          <p className="text-base font-semibold">{d.customer_name}</p>
          <p className="num text-sm text-ink-soft">{d.customer_code}</p>
          {d.customer_phone ? <p className="num text-sm">+91 {d.customer_phone}</p> : null}
          {place ? <p className="text-sm text-ink-soft">{place}</p> : null}
          {d.nominee_name ? (
            <p className="mt-2 text-2xs text-ink-soft">
              Nominee: <span className="text-ink">{d.nominee_name}</span>
              {d.nominee_relation ? ` (${d.nominee_relation})` : ''}
            </p>
          ) : null}
          {d.introducer_name || d.salesperson_name ? (
            <p className="text-2xs text-ink-soft">
              {d.introducer_name ? `Introduced by ${d.introducer_name}` : ''}
              {d.introducer_name && d.salesperson_name ? ' · ' : ''}
              {d.salesperson_name ? `Enrolled by ${d.salesperson_name}` : ''}
            </p>
          ) : null}
        </div>

        <div className="rounded-xl border border-silver-100 bg-surface p-4 shadow-card">
          <div className="mb-1 flex items-center justify-between">
            <p className="text-2xs font-semibold uppercase tracking-wide text-ink-soft">Scheme</p>
            <span className={`rounded px-1.5 py-0.5 text-2xs font-medium ${STATUS_STYLE[d.status].cls}`}>{STATUS_STYLE[d.status].label}</span>
          </div>
          <p className="text-base font-semibold">{d.scheme_name}</p>
          <p className="num text-sm text-ink-soft">{d.chit_no}</p>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-sm">
            <dt className="text-ink-soft">Type</dt>
            <dd>{isWeight ? `Gold weight (${d.purity_code})` : 'Amount'}</dd>
            <dt className="text-ink-soft">Instalment</dt>
            <dd className="num">
              {money(d.instalment_amount)} {d.period.toLowerCase()}
            </dd>
            <dt className="text-ink-soft">Joined</dt>
            <dd className="num">{date(d.join_date)}</dd>
            <dt className="text-ink-soft">Matures</dt>
            <dd className="num">{date(d.maturity_date)}</dd>
            <dt className="text-ink-soft">Bonus</dt>
            <dd>{bonusText}</dd>
            {Number(d.wastage_discount_pct) > 0 ? (
              <>
                <dt className="text-ink-soft">Wastage off</dt>
                <dd className="num">{d.wastage_discount_pct}%</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>

      {/* Actions. */}
      <div className="flex flex-wrap items-center gap-2">
        {d.status === 'ACTIVE' && can('chit.collect') ? (
          <button className="btn-primary" onClick={onCollect}>
            Take instalment
          </button>
        ) : null}
        {d.status === 'MATURED' && can('chit.settle') ? (
          <button className="btn-primary" onClick={() => onSettle('redeem')}>
            Redeem · {money(d.settlement.redeemable)}
          </button>
        ) : null}
        {['ACTIVE', 'MATURED'].includes(d.status) && anyPaid && can('chit.undo') ? (
          <button className="btn-ghost" onClick={() => onSettle('undo')}>
            Undo last payment
          </button>
        ) : null}
        {d.status === 'ACTIVE' && can('chit.settle') ? (
          <button className="btn-quiet text-danger" onClick={() => onSettle('break')}>
            Close early
          </button>
        ) : null}
        {d.status === 'REDEEMED' ? (
          <p className="text-sm text-ink-soft">
            Redeemed {d.closed_at ? date(d.closed_at) : ''} for <span className="num font-medium">{money(d.redeemed_value)}</span> —
            now on the customer’s account to spend at the till.
          </p>
        ) : null}
        {d.status === 'BROKEN' ? (
          <p className="text-sm text-ink-soft">
            Closed early: refunded <span className="num font-medium">{money(d.refund_amount)}</span>
            {Number(d.penalty_amount) > 0 ? `, kept ${money(d.penalty_amount)}` : ''}. {d.closing_reason}
          </p>
        ) : null}
      </div>

      {/* The schedule. */}
      <div className="overflow-hidden rounded-xl border border-silver-100 bg-surface shadow-card">
        <div className="max-h-[48vh] overflow-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">#</th>
                <th className="th">Due</th>
                <th className="th">Paid on</th>
                <th className="th text-right">Amount</th>
                {isWeight ? <th className="th text-right">Rate / g</th> : null}
                {isWeight ? <th className="th text-right">Gold</th> : null}
                <th className="th">Mode</th>
                <th className="th">Receipt</th>
                <th className="th">By</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((i) => (
                <tr
                  key={i.instalment_no}
                  className={i.status === 'PAID' ? 'bg-success/5' : i.overdue && d.status === 'ACTIVE' ? 'bg-danger/5' : ''}
                >
                  <td className="td num">{i.instalment_no}</td>
                  <td className="td num">{date(i.due_date)}</td>
                  <td className="td num">
                    {i.paid_on ? (
                      date(i.paid_on)
                    ) : i.overdue && d.status === 'ACTIVE' ? (
                      <span className="text-2xs font-medium text-danger">overdue</span>
                    ) : (
                      <span className="text-ink-faint">—</span>
                    )}
                  </td>
                  <td className="td num text-right">{money(i.paid_amount ?? i.due_amount)}</td>
                  {isWeight ? <td className="td num text-right">{i.rate_per_gram ? money(i.rate_per_gram) : ''}</td> : null}
                  {isWeight ? <td className="td num text-right">{i.weight_gms ? fine(i.weight_gms) : ''}</td> : null}
                  <td className="td text-2xs">
                    {i.pay_mode ?? ''}
                    {i.reference_no ? <span className="num block text-ink-faint">{i.reference_no}</span> : null}
                  </td>
                  <td className="td">
                    {i.receipt_no ? (
                      <button className="num text-2xs text-gold-700 underline" onClick={() => onReceipt(i.instalment_no)}>
                        {i.receipt_no}
                      </button>
                    ) : null}
                  </td>
                  <td className="td text-2xs text-ink-soft">{i.received_by ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The summary strip. */}
        <div className="grid grid-cols-2 gap-px border-t border-silver-100 bg-silver-100 md:grid-cols-5">
          <Sum label="Instalments" value={`${d.paid_count} / ${schedule.length}`} />
          <Sum label="Paid" value={`${money(d.paid_amount)}`} sub={`of ${money(total.toFixed(2))}`} />
          <Sum label="Remaining" value={money(remaining.toFixed(2))} tone={remaining > 0 ? 'warn' : undefined} />
          <Sum
            label={isWeight ? 'Gold saved' : 'Bonus at maturity'}
            value={isWeight ? fine(d.paid_weight) : money(d.settlement.bonus)}
            sub={isWeight && d.settlement.currentRate ? `worth ${money((Number(d.paid_weight) * Number(d.settlement.currentRate)).toFixed(2))} today` : undefined}
          />
          <Sum label="Redeemable now" value={money(d.settlement.redeemable)} tone="good" sub={d.status === 'ACTIVE' ? 'at maturity' : undefined} />
        </div>
      </div>
    </div>
  );
}

function Sum({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'warn' }) {
  return (
    <div className="bg-surface px-3 py-2">
      <p className="text-2xs text-ink-soft">{label}</p>
      <p className={`num font-semibold ${tone === 'good' ? 'text-success' : tone === 'warn' ? 'text-warn' : ''}`}>{value}</p>
      {sub ? <p className="text-2xs text-ink-faint">{sub}</p> : null}
    </div>
  );
}
